import { useState, useEffect } from 'react';
import { getIntegrationToken } from 'services/integration-service';
import { useConfig } from './use-config';

/**
 * Custom hook to manage integration token for external service connections
 *
 * This hook handles the retrieval and state management of JWT tokens used for
 * third-party integrations. It automatically fetches the token when the
 * configuration settings are available and provides loading state management.
 *
 * Features:
 * - Automatic token fetching on configuration change
 * - Loading state management
 * - Error handling with console logging
 * - Memoized token value to prevent unnecessary re-renders
 *
 * @returns {Object} The token state and loading status
 * @returns {string | undefined} token - The JWT token for integration services
 * @returns {boolean} isLoadingToken - Whether the token is currently being fetched
 *
 * @example
 * const { token, isLoadingToken } = useIntegrationToken();
 * 
 * if (isLoadingToken) {
 *   return <LoadingSpinner />;
 * }
 * 
 * return (
 *   <IntegrationProvider token={token}>
 *     <MyComponent />
 *   </IntegrationProvider>
 * );
 */
export const useIntegrationToken = () => {
    const [token, setToken] = useState<string | undefined>(undefined);
    const [isLoadingToken, setIsLoadingToken] = useState(true);
    const { configurationSettings } = useConfig();

    useEffect(() => {
        const initToken = async () => {
            try {
                const jwtToken = await getIntegrationToken(configurationSettings);
                setToken(jwtToken.token);
            } catch (error) {
                console.error('Failed to generate integration token:', error);
                setToken(undefined);
            } finally {
                setIsLoadingToken(false);
            }
        };

        if (configurationSettings) {
            initToken();
        }
    }, [configurationSettings]);

    return {
        token,
        isLoadingToken
    };
};
